import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { manifesto } from '@/mocks/sections';
import onView from '@/hooks/onView';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export default function ManifestoSection() {
  const ref = useRef<HTMLElement>(null);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const q = gsap.utils.selector(ref);
      const tl = gsap.timeline({ paused: true });
      tl.fromTo(q('[data-m-eyebrow]'), { y: 14, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, 0)
        .fromTo(q('[data-m-line]'), { yPercent: 105, y: 28 }, { yPercent: 0, y: 0, duration: 1.15, ease: 'power3.out' }, 0.08)
        .fromTo(q('[data-m-big]'), { clipPath: 'inset(0% 0% 100% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.05, ease: 'power3.inOut' }, 0.2)
        .fromTo(q('[data-m-big] img'), { y: 22 }, { y: 0, duration: 1.3, ease: 'power3.out' }, 0.2)
        .fromTo(q('[data-m-para]'), { y: 18, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, ease: 'power3.out', stagger: 0.1 }, 0.45)
        .fromTo(q('[data-m-small]'), { y: 24, opacity: 0, clipPath: 'inset(100% 0% 0% 0%)' }, { y: 0, opacity: 1, clipPath: 'inset(0% 0% 0% 0%)', duration: 1, ease: 'power3.inOut' }, 0.55);
      return onView(ref.current, () => tl.restart(), () => tl.pause(0), '0px 0px -25% 0px');
    });
    mm.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', () => {
      const q = gsap.utils.selector(ref);
      const st = { trigger: ref.current, start: 'top bottom', end: 'bottom top', scrub: 1 };
      gsap.fromTo(q('[data-px-big]'), { y: 12 }, { y: -12, ease: 'none', scrollTrigger: st });
      gsap.fromTo(q('[data-px-small]'), { y: 20 }, { y: -20, ease: 'none', scrollTrigger: st });
      gsap.fromTo(q('[data-px-text]'), { y: 8 }, { y: -8, ease: 'none', scrollTrigger: st });
    });
  }, { scope: ref });

  return (
    <section ref={ref} id="about" className="bg-background-50 px-4 md:px-10 py-20 md:py-36">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7 min-w-0">
          <div data-px-text>
            <p data-m-eyebrow className="text-[11px] uppercase tracking-[0.24em] text-primary-600">{manifesto.eyebrow}</p>
            <h2 className="mt-6 font-heading font-light text-4xl md:text-7xl leading-[1.05] text-primary-900">
              <span className="block overflow-hidden pb-[0.08em]">
                <span data-m-line className="block">{manifesto.title}</span>
              </span>
            </h2>
            <div className="mt-10 md:mt-16 border-t border-background-300/70 pt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              <p data-m-para className="font-heading text-lg md:text-xl leading-relaxed text-foreground-900">{manifesto.lead}</p>
              <p data-m-para className="text-sm leading-relaxed text-foreground-600">{manifesto.body}</p>
            </div>
          </div>
          <div data-px-small className="hidden md:block mt-14 w-[60%]">
            <div data-m-small className="w-full h-[300px] rounded-none overflow-hidden">
              <img loading="lazy" decoding="async" src={manifesto.imageSmall} alt="Window frame detail" title="Window installation detail" className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 lg:pt-24">
          <div data-px-big>
            <div data-m-big className="w-full h-[460px] md:h-[680px] rounded-none overflow-hidden">
              <img loading="lazy" decoding="async" src={manifesto.image} alt="Living room with window to green foliage" title="Natural light through new windows" className="w-full h-full object-cover object-top scale-[1.04]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}